import { translate, useT } from '../i18n'

/**
 * Etat d'erreur partage.
 *
 * Regle metier : cet outil sert a la conformite. On n'affiche JAMAIS de donnees
 * de substitution quand l'API echoue — un agent pourrait agir sur des alertes
 * inventees. On affiche l'echec et on propose de reessayer.
 */
export function ErrorState({
  message,
  onRetry,
  compact,
}: {
  message?: string | null
  onRetry?: () => void
  compact?: boolean
}) {
  const t = useT()
  return (
    <div className={compact ? 'error-state error-state-compact' : 'error-state'} role="alert">
      <span className="error-state-icon" aria-hidden="true">⚠</span>
      <div className="error-state-body">
        <p className="error-state-title">{t('error.title')}</p>
        <p className="error-state-msg">{message || t('error.noAnswer')}</p>
      </div>
      {onRetry && (
        <button type="button" className="btn btn-secondary error-state-retry" onClick={onRetry}>
          {t('common.retry')}
        </button>
      )}
    </div>
  )
}

/**
 * Extrait un message lisible d'une erreur inconnue.
 *
 * Les messages bruts du navigateur ("Failed to fetch") et les codes HTTP nus
 * ne disent rien a un utilisateur metier : on les traduit en langage clair.
 *
 * `translate` et non `useT` : la fonction est appelee hors rendu, dans des
 * gestionnaires d'erreur. Le message obtenu est stocke dans l'etat ; un
 * changement de langue le laisse tel quel jusqu'au prochain chargement, les
 * ecrans etant remontes a ce moment-la.
 */
export function errorMessage(err: unknown): string {
  const raw =
    err instanceof Error ? err.message : typeof err === 'string' ? err : ''

  if (!raw) return translate('error.unknown')

  if (/failed to fetch|networkerror|load failed/i.test(raw)) {
    return translate('error.unreachable')
  }
  // Seuls les appels bruts laissent passer un « API 4xx » : le client HTTP, lui,
  // rend déjà un message lisible, qui ressort tel quel plus bas.
  if (/^API 401/i.test(raw)) {
    return translate('error.sessionExpired')
  }
  if (/^API 403/i.test(raw)) {
    return translate('error.forbidden')
  }
  if (/^API 404/i.test(raw)) {
    return translate('error.notFound')
  }
  if (/^API 429/i.test(raw)) {
    return translate('error.tooManyRequests')
  }
  if (/^API 5\d\d/i.test(raw)) {
    return translate('error.server')
  }
  return raw
}
